import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Clock, CalendarDays } from "lucide-react";
import i18n from "@/lib/i18n";
import { Logo, ThemeToggle } from "@/components/Navbar";
import { overlayGradient } from "@/lib/gameStyle";
import { resolveImageUrl } from "@/lib/images";
import { frameStyle } from "@/components/ContentFrame";
import { useResponsiveBackground } from "@/lib/useResponsiveBackground";

function formatStart(dateStr, timeStr, lang) {
  if (!dateStr) return null;
  const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString(lang.startsWith("fr") ? "fr-FR" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  if (!timeStr || timeStr === "00:00") return day;
  return lang.startsWith("fr") ? `${day} à ${timeStr.replace(":", "h")}` : `${day} at ${timeStr}`;
}

// Live countdown to `opensAt` (ISO with offset) → { d, h, m, s } or null once reached.
function useCountdown(opensAt) {
  const target = opensAt ? new Date(opensAt).getTime() : NaN;
  const compute = () => {
    if (Number.isNaN(target)) return null;
    const diff = Math.max(0, target - Date.now());
    if (diff <= 0) return null;
    const s = Math.floor(diff / 1000);
    return {
      d: Math.floor(s / 86400),
      h: Math.floor((s % 86400) / 3600),
      m: Math.floor((s % 3600) / 60),
      s: s % 60,
    };
  };
  const [left, setLeft] = useState(compute);
  useEffect(() => {
    if (Number.isNaN(target)) return undefined;
    const id = setInterval(() => setLeft(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return left;
}

function CountdownUnit({ value, label, light }) {
  return (
    <div className="flex flex-col items-center min-w-[52px] sm:min-w-[64px]" data-testid={`waiting-countdown-${label}`}>
      <span
        className="tabular-nums text-2xl sm:text-4xl font-semibold leading-none"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {String(value).padStart(2, "0")}
      </span>
      <span className={`mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] ${light ? "text-white/60" : "text-muted-foreground"}`}>
        {label}
      </span>
    </div>
  );
}

/**
 * Waiting screen shown when no game is open yet: merchant hub without a live
 * game, or a direct /play link to a scheduled game. When an upcoming game is
 * known, its background visuals are used so the page already feels branded.
 */
export default function GameWaitingScreen({ merchant = {}, upcoming = null }) {
  const { t } = useTranslation();
  const lang = i18n.language || "en";
  const fr = lang.startsWith("fr");
  const bgImage = useResponsiveBackground(upcoming);

  // Speak the upcoming game's language (restored when leaving the page).
  useEffect(() => {
    const target = upcoming?.language;
    if (!target || i18n.language === target) return undefined;
    const previous = i18n.language;
    i18n.changeLanguage(target);
    return () => {
      i18n.changeLanguage(previous);
    };
  }, [upcoming?.language]);
  const merchantName = merchant.company || merchant.name || "";
  const badge = merchant.pause_badge || merchantName || (fr ? "Enseigne QRISE" : "QRISE brand");
  const title = merchant.pause_title || (fr ? "Le prochain jeu arrive bientôt" : "A new game is coming soon");
  const body =
    merchant.pause_body ||
    (fr
      ? `${merchantName ? `${merchantName} prépare` : "Ce commerçant prépare"} une nouvelle expérience. Revenez dans quelques instants et scannez à nouveau ce QR code pour tenter votre chance.`
      : `${merchantName ? `${merchantName} is preparing` : "This brand is preparing"} a new experience. Come back in a few moments and scan this QR code again for a chance to win.`);
  const status =
    merchant.pause_status ||
    (upcoming ? (fr ? "Bientôt disponible" : "Coming soon") : fr ? "En pause" : "Paused");
  const startLabel = formatStart(upcoming?.start_date, upcoming?.start_time, lang);
  const left = useCountdown(upcoming?.opens_at);
  const units = left
    ? [
        [left.d, t("play.waiting_unit_days")],
        [left.h, t("play.waiting_unit_hours")],
        [left.m, t("play.waiting_unit_minutes")],
        [left.s, t("play.waiting_unit_seconds")],
      ]
    : [];

  const frame = upcoming?.content_frame || null;
  const dark = !!bgImage && !frame;
  return (
    <div
      className={`min-h-screen bg-background relative overflow-hidden ${dark ? "dark text-foreground" : ""}`}
      data-testid="merchant-empty-state"
    >
      {bgImage ? (
        <>
          <div
            className="fixed inset-0 bg-cover bg-center pointer-events-none"
            style={{ backgroundImage: `url(${resolveImageUrl(bgImage)})` }}
            data-testid="waiting-bg-image"
          />
          <div className="fixed inset-0 pointer-events-none" style={{ background: overlayGradient(upcoming) }} />
        </>
      ) : (
        <div className="fixed inset-0 pointer-events-none qrise-ambient-aqua" />
      )}

      <header className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        {!bgImage && <ThemeToggle />}
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-24 text-center fade-up">
        {merchant.logo_url && (
          <div className="mx-auto mb-5 h-20 w-20 rounded-full bg-white shadow-xl ring-4 ring-white/20 overflow-hidden flex items-center justify-center">
            <img src={merchant.logo_url} alt={merchantName} className="h-full w-full object-cover" data-testid="waiting-merchant-logo" />
          </div>
        )}
        <div
          style={frame ? frameStyle(frame) : undefined}
          className={`${frame ? "px-6 py-8 sm:px-10 sm:py-10" : dark ? "rounded-3xl bg-black/55 backdrop-blur-xl border border-white/10 px-6 py-8 sm:px-10 sm:py-10 text-white shadow-2xl" : ""}`}
          data-testid="waiting-card"
        >
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-6 ${
              dark ? "border-white/20 bg-white/10 text-white/80" : "border-border bg-card/60 backdrop-blur text-muted-foreground"
            }`}
            data-testid="merchant-pause-badge"
          >
            <Sparkles className="h-3 w-3" />
            {badge}
          </div>

          <h1
            className="text-4xl sm:text-5xl font-semibold tracking-tight"
            style={bgImage && upcoming?.title_color ? { color: upcoming.title_color } : undefined}
            data-testid="merchant-pause-title"
          >
            {title}
          </h1>

          <p
            className={`mt-5 leading-relaxed max-w-lg mx-auto whitespace-pre-line ${dark ? "text-white/85" : frame ? "text-neutral-800" : "text-muted-foreground"}`}
            data-testid="merchant-pause-body"
          >
            {body}
          </p>

          {upcoming && (
            <div className="mt-8 flex justify-center">
              <div
                className={`inline-flex flex-col items-center gap-1.5 rounded-2xl px-5 py-3.5 ${
                  dark ? "bg-white/10 border border-white/15" : "bg-card/70 border border-border"
                }`}
                data-testid="waiting-upcoming-card"
              >
                {upcoming.title && (
                  <div className="text-sm font-bold tracking-tight" data-testid="waiting-upcoming-title">
                    {upcoming.title}
                  </div>
                )}
                {startLabel && (
                  <div
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${dark ? "text-white/75" : "text-muted-foreground"}`}
                    data-testid="waiting-start-date"
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    {t("play.waiting_opens_on", { date: startLabel })}
                  </div>
                )}
                {left && (
                  <div
                    className={`mt-3 flex items-start divide-x ${dark ? "divide-white/15" : "divide-border"}`}
                    data-testid="waiting-countdown"
                    aria-label={t("play.waiting_countdown_label")}
                  >
                    {units.map(([v, label]) => (
                      <CountdownUnit key={label} value={v} label={label} light={dark} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div
            className={`mt-8 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.24em] font-bold ${dark ? "text-white/60" : "text-muted-foreground"}`}
            data-testid="merchant-pause-status"
          >
            <Clock className="h-3.5 w-3.5" />
            {status}
          </div>
        </div>

        <div className="mt-12">
          <span
            className={`text-[11px] ${frame ? "inline-block rounded-full bg-black/55 px-3 py-1 text-white/85" : dark ? "text-white/60" : "text-muted-foreground"}`}
            data-testid="waiting-powered"
          >
            {t("play.powered")}
          </span>
        </div>
      </main>
    </div>
  );
}
